import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Lock,
  Sparkles,
  Star,
} from "lucide-react";
import Navbar from "@/components/shared/Navbar";
import StepIndicator, { Step } from "@/components/shared/StepIndicator";
import {
  PLANS,
  priceFor,
  currency,
  BILLING_LABEL,
  PlanPricing,
} from "@/lib/pricing";
import type { BillingId, PlanId } from "@/config/checkoutConfig";
import { getCheckoutUrl } from "@/config/checkoutConfig";

const STEPS: Step[] = [
  { id: 1, label: "Choose Your Plan" },
  { id: 2, label: "Choose Billing" },
  { id: 3, label: "Secure Checkout" },
];

const BILLINGS: { id: BillingId; label: string; badge?: string }[] = [
  { id: "monthly", label: "Monthly", badge: "Most flexible" },
  { id: "annual", label: "Annual", badge: "Best value" },
  { id: "fivePay", label: "5 Easy Payments" },
];

export default function EnrollmentPage() {
  const [params] = useSearchParams();
  // Backwards compat: allow ?plan=dfy from any old link → premium.
  const rawPlan = params.get("plan");
  const initialPlan = ((rawPlan === "dfy" ? "premium" : rawPlan) as PlanId | null) ?? null;
  const initialBilling = (params.get("billing") as BillingId | null) ?? null;

  const [plan, setPlan] = useState<PlanId | null>(initialPlan);
  const [billing, setBilling] = useState<BillingId | null>(initialBilling);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    document.title = "Enroll · WinCampaign DFY Growth Accelerator™";
    window.scrollTo(0, 0);
  }, []);

  const currentStep = useMemo(() => {
    if (!plan) return 1;
    if (!billing) return 2;
    return 3;
  }, [plan, billing]);

  const selectedPlanObj = plan ? PLANS.find((p) => p.id === plan) : null;
  const canContinue = Boolean(plan && billing);

  const handleContinue = () => {
    if (!plan || !billing) return;
    setRedirecting(true);
    const url = getCheckoutUrl(plan, billing);
    // Small delay for UX so the loading state is visible.
    window.setTimeout(() => {
      window.location.href = url;
    }, 350);
  };

  return (
    <div data-testid="wc-enrollment-page" className="min-h-screen bg-[#f8f6ff]">
      <Navbar />

      <main className="pt-28 pb-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          {/* Header */}
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white text-[#673dfc] border border-[#e5dfff] font-nav font-semibold text-xs rounded-full px-3 py-1 mb-4">
              <Sparkles size={13} /> Enrollment
            </div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#30265b] leading-[1.1]">
              Let&apos;s get your growth engine running.
            </h1>
            <p className="mt-4 text-base sm:text-lg text-gray-600">
              Three quick steps. Payment is handled securely by SureCart.
            </p>
          </div>

          <div className="mt-10">
            <StepIndicator steps={STEPS} current={currentStep} />
          </div>

          {/* Step 1 — plans */}
          <section
            data-testid="wc-enroll-step-1"
            className="mt-12"
            aria-labelledby="step-1-title"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-8 rounded-full bg-[#30265b] text-[#d3fc3a] font-display font-bold grid place-items-center">
                1
              </span>
              <h2
                id="step-1-title"
                className="font-display font-bold text-2xl text-[#30265b]"
              >
                Choose your plan
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {PLANS.map((p) => (
                <PlanCard
                  key={p.id}
                  plan={p}
                  selected={plan === p.id}
                  onSelect={() => setPlan(p.id)}
                />
              ))}
            </div>
          </section>

          {/* Step 2 — billing */}
          <section
            data-testid="wc-enroll-step-2"
            className={`mt-14 transition-opacity ${plan ? "opacity-100" : "opacity-50 pointer-events-none"}`}
            aria-labelledby="step-2-title"
          >
            <div className="flex items-center gap-3 mb-6">
              <span
                className={`w-8 h-8 rounded-full font-display font-bold grid place-items-center ${
                  plan ? "bg-[#30265b] text-[#d3fc3a]" : "bg-gray-200 text-gray-400"
                }`}
              >
                2
              </span>
              <h2
                id="step-2-title"
                className="font-display font-bold text-2xl text-[#30265b]"
              >
                Choose your billing
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {BILLINGS.map((b) => {
                const price = selectedPlanObj ? priceFor(selectedPlanObj, b.id) : 0;
                const isSelected = billing === b.id;
                return (
                  <button
                    key={b.id}
                    data-testid={`wc-enroll-billing-${b.id}`}
                    onClick={() => setBilling(b.id)}
                    disabled={!plan}
                    className={`text-left rounded-3xl p-6 border-2 transition-all duration-200 bg-white ${
                      isSelected
                        ? "border-[#673dfc] shadow-lg shadow-[#673dfc]/10 -translate-y-1"
                        : "border-gray-200 hover:border-[#c5b4ff]"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-display font-bold text-lg text-[#30265b]">
                        {b.label}
                      </div>
                      {b.badge && (
                        <span className="text-[10px] font-nav font-semibold uppercase tracking-wider bg-[#d3fc3a] text-[#1a1a24] px-2 py-0.5 rounded-full">
                          {b.badge}
                        </span>
                      )}
                    </div>
                    <div className="mt-3 font-display font-extrabold text-3xl text-[#30265b]">
                      {selectedPlanObj ? currency(price) : "—"}
                    </div>
                    <div className="text-xs font-nav text-gray-500 mt-1">
                      {b.id === "monthly" && "billed monthly"}
                      {b.id === "annual" && "billed once per year"}
                      {b.id === "fivePay" && "5 monthly installments"}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Step 3 — summary + continue */}
          <section
            data-testid="wc-enroll-step-3"
            className={`mt-14 transition-opacity ${canContinue ? "opacity-100" : "opacity-60"}`}
            aria-labelledby="step-3-title"
          >
            <div className="flex items-center gap-3 mb-6">
              <span
                className={`w-8 h-8 rounded-full font-display font-bold grid place-items-center ${
                  canContinue ? "bg-[#30265b] text-[#d3fc3a]" : "bg-gray-200 text-gray-400"
                }`}
              >
                3
              </span>
              <h2
                id="step-3-title"
                className="font-display font-bold text-2xl text-[#30265b]"
              >
                Secure checkout
              </h2>
            </div>

            <div className="rounded-3xl bg-white border border-gray-200 p-6 sm:p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <SummaryLine
                  label="Plan"
                  value={selectedPlanObj ? selectedPlanObj.name : "—"}
                />
                <SummaryLine
                  label="Billing"
                  value={billing ? BILLING_LABEL[billing] : "—"}
                />
                <SummaryLine
                  label="Amount"
                  value={
                    selectedPlanObj && billing
                      ? currency(priceFor(selectedPlanObj, billing))
                      : "—"
                  }
                  strong
                />
              </div>

              <div className="mt-8 flex flex-col-reverse sm:flex-row items-center justify-between gap-4">
                <Link
                  to="/"
                  data-testid="wc-enroll-back"
                  className="text-sm font-nav font-medium text-gray-500 hover:text-[#30265b] flex items-center gap-2"
                >
                  <ArrowLeft size={14} /> Back to landing page
                </Link>

                <button
                  data-testid="wc-enroll-continue"
                  onClick={handleContinue}
                  disabled={!canContinue || redirecting}
                  className={`font-display font-semibold rounded-full px-8 py-4 flex items-center gap-2 transition-all duration-300 ${
                    canContinue && !redirecting
                      ? "bg-[#d3fc3a] text-[#1a1a24] hover:-translate-y-1 hover:shadow-2xl"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  <Lock size={16} />
                  {redirecting ? "Redirecting…" : "Continue to secure checkout"}
                  {!redirecting && <ArrowRight size={16} />}
                </button>
              </div>

              <div className="mt-6 text-xs text-gray-500 text-center flex items-center justify-center gap-2">
                <Lock size={12} /> Payments handled securely by SureCart · Stripe
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

function PlanCard({
  plan,
  selected,
  onSelect,
}: {
  plan: PlanPricing;
  selected: boolean;
  onSelect: () => void;
}) {
  const isElite = plan.featured;
  return (
    <button
      data-testid={`wc-enroll-plan-${plan.id}`}
      onClick={onSelect}
      className={`relative text-left rounded-3xl p-7 sm:p-8 border-2 transition-all duration-300 ${
        selected
          ? isElite
            ? "bg-[#30265b] text-white border-[#d3fc3a] shadow-2xl shadow-[#673dfc]/30 -translate-y-1"
            : "bg-white border-[#673dfc] shadow-lg shadow-[#673dfc]/10 -translate-y-1"
          : isElite
          ? "bg-[#30265b] text-white border-[#673dfc]/40 hover:border-[#d3fc3a]"
          : "bg-white border-gray-200 hover:border-[#c5b4ff]"
      }`}
    >
      {isElite && (
        <div className="absolute -top-3 left-6 bg-[#d3fc3a] text-[#1a1a24] text-[10px] font-display font-bold uppercase tracking-wider py-1 px-3 rounded-full flex items-center gap-1">
          <Star size={11} fill="#1a1a24" /> Most Popular
        </div>
      )}
      <div
        className={`text-xs font-nav font-semibold uppercase tracking-wider ${
          isElite ? "text-[#d3fc3a]" : "text-[#673dfc]"
        }`}
      >
        {isElite ? "ELITE" : "PREMIUM"}
      </div>
      <div
        className={`mt-2 font-display font-bold text-xl sm:text-2xl leading-tight ${
          isElite ? "text-white" : "text-[#30265b]"
        }`}
      >
        {plan.name}
      </div>
      <p
        className={`mt-2 text-sm leading-relaxed ${
          isElite ? "text-white/70" : "text-gray-600"
        }`}
      >
        {plan.tagline}
      </p>

      <div className="mt-5 flex items-baseline gap-2">
        <span
          className={`font-display font-extrabold text-4xl ${
            isElite ? "text-white" : "text-[#30265b]"
          }`}
        >
          {currency(plan.monthly)}
        </span>
        <span
          className={`font-nav text-sm ${
            isElite ? "text-white/70" : "text-gray-500"
          }`}
        >
          /month
        </span>
      </div>

      <ul className="mt-5 space-y-2">
        {plan.bullets.slice(0, 5).map((b) => (
          <li
            key={b}
            className={`flex items-start gap-2.5 text-sm ${
              isElite ? "text-white/90" : "text-gray-700"
            }`}
          >
            <span
              className={`w-4 h-4 mt-0.5 rounded-full grid place-items-center flex-shrink-0 ${
                isElite ? "bg-[#d3fc3a] text-[#1a1a24]" : "bg-[#673dfc] text-white"
              }`}
            >
              <Check size={10} strokeWidth={3} />
            </span>
            {b}
          </li>
        ))}
      </ul>

      <div
        className={`mt-6 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-display font-semibold ${
          selected
            ? isElite
              ? "bg-[#d3fc3a] text-[#1a1a24]"
              : "bg-[#673dfc] text-white"
            : isElite
            ? "bg-white/10 text-white"
            : "bg-[#f3efff] text-[#673dfc]"
        }`}
      >
        {selected ? (
          <>
            <Check size={12} strokeWidth={3} /> Selected
          </>
        ) : (
          "Select this plan"
        )}
      </div>
    </button>
  );
}

function SummaryLine({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div>
      <div className="text-xs font-nav font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div
        className={`mt-1 font-display ${
          strong
            ? "font-extrabold text-3xl text-[#30265b]"
            : "font-bold text-lg text-[#30265b]"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
